import React, { useState, useEffect } from 'react';
import { FaArrowRight, FaArrowLeft, FaTimes, FaCheckCircle, FaPlay, FaStream } from 'react-icons/fa';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';

const TourOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TourCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 30px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  position: relative;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const TourHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const TourTitle = styled.h2`
  color: #2d3748;
  font-size: 1.5rem;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #718096;
  cursor: pointer;
  padding: 5px;
  
  &:hover {
    color: #2d3748;
  }
`;

const TourContent = styled.div`
  margin-bottom: 30px;
`;

const TourDescription = styled.p`
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const FeatureDemo = styled.div`
  background: #f7fafc;
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
  border-left: 4px solid #0078D4;
`;

const FeatureTitle = styled.h4`
  color: #2d3748;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FeatureDescription = styled.p`
  color: #4a5568;
  margin: 0;
  font-size: 0.9rem;
`;

const TourNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NavButton = styled.button`
  background: ${props => props.primary ? '#0078D4' : 'transparent'};
  color: ${props => props.primary ? 'white' : '#0078D4'};
  border: 2px solid #0078D4;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.primary ? '#005a9e' : '#f7fafc'};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ProgressDots = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.active ? '#0078D4' : '#e2e8f0'};
  transition: background 0.3s ease;
`;

const SkipButton = styled.button`
  background: none;
  border: none;
  color: #718096;
  cursor: pointer;
  font-size: 0.9rem;
  text-decoration: underline;
  
  &:hover {
    color: #2d3748;
  }
`;

const OnboardingTour = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const history = useHistory();
  
  const tourSteps = [
    {
      title: "Welcome to Rundoit! 🎉",
      description: "Let's take a quick tour to help you get started with your productivity journey. This will only take 2 minutes!",
      feature: null
    },
    {
      title: "Meet Your Productivity Modes",
      description: "Rundoit offers three powerful modes to eliminate decision fatigue and boost your focus:",
      feature: {
        title: "Random Mode",
        icon: <span style={{fontSize: '16px'}}>🎲</span>,
        description: "Perfect when you can't decide what to do next. Let the system surprise you!"
      }
    },
    {
      title: "Start Mode",
      description: "Begin with confidence using our structured approach:",
      feature: {
        title: "Start Mode",
        icon: <FaPlay />,
        description: "Tackle tasks in logical order, starting with the most important ones."
      }
    },
    {
      title: "Flow Mode",
      description: "Achieve peak productivity with deep focus:",
      feature: {
        title: "Flow Mode",
        icon: <FaStream />,
        description: "Enter uninterrupted work sessions for maximum efficiency and creativity."
      }
    },
    {
      title: "Ready to Get Started?",
      description: "You're all set! We've prepared some starter templates to help you begin. Complete your first 3 tasks to see the magic happen!",
      feature: null
    }
  ];

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      history.push('/features');
      onComplete && onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  if (!isOpen) return null;

  const currentStepData = tourSteps[currentStep];

  return (
    <TourOverlay>
      <TourCard>
        <TourHeader>
          <TourTitle>{currentStepData.title}</TourTitle>
          <CloseButton onClick={onClose}>
            <FaTimes />
          </CloseButton>
        </TourHeader>
        
        <TourContent>
          <TourDescription>{currentStepData.description}</TourDescription>
          
          {currentStepData.feature && (
            <FeatureDemo>
              <FeatureTitle>
                {currentStepData.feature.icon}
                {currentStepData.feature.title}
              </FeatureTitle>
              <FeatureDescription>{currentStepData.feature.description}</FeatureDescription>
            </FeatureDemo>
          )}
        </TourContent>
        
        <TourNavigation>
          <div>
            {currentStep > 0 && (
              <NavButton onClick={handlePrevious}>
                <FaArrowLeft />
                Previous
              </NavButton>
            )}
          </div>
          
          <ProgressDots>
            {tourSteps.map((_, index) => (
              <Dot key={index} active={index === currentStep} />
            ))}
          </ProgressDots>
          
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <SkipButton onClick={handleSkip}>Skip Tour</SkipButton>
            <NavButton primary onClick={handleNext}>
              {currentStep === tourSteps.length - 1 ? (
                <>
                  <FaCheckCircle />
                  Explore Features
                </>
              ) : (
                <>
                  Next
                  <FaArrowRight />
                </>
              )}
            </NavButton>
          </div>
        </TourNavigation>
      </TourCard>
    </TourOverlay>
  );
};

export default OnboardingTour;
